// The event types the event dialog offers, when an event is added and when
// one is changed. Moved here from EventPopup.jsx unchanged, with one
// addition: Vortex, among the field events.
//
// Vortex (68) cannot be recorded in either app, so it is typed on the website:
// added as an event here, or a Javelin recorded in the Field Events app is
// changed to it here (Doug, 2026-10-15; register R45 of
// docs/school-carnivals-plan.md). Floppy Jump stays display-only (R48): its
// results would be high jump attempts, and high jump is picked out by its name.

import { RECORDABLE_EVENT_CODES } from 'constants/eventTypes';

// The field events the dialog offers: the six the apps record, and Vortex.
const OFFERED_FIELD_EVENT_CODES = [61, 62, 63, 64, 65, 66, 68];

const toOption = (et) => ({ label: et.displayName, value: et.eventCode });

// Whether the dialog offers an event type for a new event.
const offeredForNewEvent = (et) => {
  if (!RECORDABLE_EVENT_CODES.includes(et.eventCode) || !et.isActive) {
    return false;
  }
  if (et.category === 'Field') {
    return OFFERED_FIELD_EVENT_CODES.includes(et.eventCode);
  }
  return true;
};

// eventTypes: the club's event types (getClubEventTypes).
// selectedEvent: the event being changed (getEventById), or null when adding.
export const eventTypeOptionsFor = ({ eventTypes, isAddMode, selectedEvent }) => {
  if (!eventTypes || eventTypes.length === 0) return [];

  if (isAddMode) {
    // Add mode - show all recordable event types
    return eventTypes.filter(offeredForNewEvent).map(toOption);
  }

  // Edit mode - filter based on current event
  if (!selectedEvent) return [];

  // High Jump events cannot be changed to other event types
  if (selectedEvent.eventType === 'High Jump') {
    return [{ label: 'High Jump', value: 61 }];
  }

  // Training events cannot be changed to other event types
  if (selectedEvent.eventCategory === 'Training') {
    return eventTypes
      .filter((et) =>
        et.category === 'Training' &&
        RECORDABLE_EVENT_CODES.includes(et.eventCode) &&
        et.isActive
      )
      .map(toOption);
  }

  // Track events - filter by category
  if (selectedEvent.eventCategory === 'Track') {
    return eventTypes
      .filter((et) =>
        et.category === 'Track' &&
        RECORDABLE_EVENT_CODES.includes(et.eventCode) &&
        et.isActive
      )
      .map(toOption);
  }

  // For Field events, only include specific event types that are visible
  if (selectedEvent.eventCategory === 'Field') {
    return eventTypes
      .filter((et) =>
        et.category === 'Field' &&
        OFFERED_FIELD_EVENT_CODES.includes(et.eventCode) &&
        RECORDABLE_EVENT_CODES.includes(et.eventCode) &&
        et.isActive &&
        et.displayName !== 'High Jump'
      )
      .map(toOption);
  }

  // Fallback for other categories
  return eventTypes
    .filter((et) =>
      et.category === selectedEvent.eventCategory &&
      RECORDABLE_EVENT_CODES.includes(et.eventCode) &&
      et.isActive
    )
    .map(toOption);
};

// The events a club record can be added for by hand (Club Records, + Add
// Record): the ones this dialog offers for a new event, without training, in
// the club's order (the server sends them so). Valued by name, as a record is
// stored. In place of a fixed list of 28 names that missed the newer events and
// offered four the server refuses (docs/school-carnivals-step-3-relays.md,
// 11.6, item 2).
export const clubRecordEventOptions = (eventTypes) =>
  (eventTypes || [])
    .filter((et) => offeredForNewEvent(et) && et.category !== 'Training')
    .map((et) => ({ label: et.displayName, value: et.displayName }));
